import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";

import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";

import {
  uploadAreaSx,
  fileContainerSx,
} from "./FileUploadArea.styles";

import documentAnalyzerService from "../../../../../../../../services/documentAnalyzerService";

const FileUploadArea = ({
  onArchivoSeleccionado,
  onDocumentoAnalizado,
  onArchivoEliminado,

  /*
   * Información recuperada desde el hook.
   *
   * No contiene una instancia real de File.
   * Solo representa visualmente un documento
   * que ya fue analizado anteriormente.
   */
  archivoPersistido = null,
}) => {
  const inputRef = useRef(null);

  /*
   * Archivo real seleccionado durante el montaje
   * actual del componente.
   */
  const [archivo, setArchivo] =
    useState(null);

  const [analizando, setAnalizando] =
    useState(false);

  const [error, setError] =
    useState(null);

  // ============================================================
  // INFORMACIÓN DEL ARCHIVO VISIBLE
  // ============================================================

  /*
   * Al seleccionar un archivo real, se muestran sus
   * metadatos locales.
   *
   * Al regresar a la vista, se muestran los metadatos
   * persistidos recibidos desde el hook.
   *
   * En ningún caso se vuelve a analizar automáticamente
   * un archivo persistido.
   */
  const infoArchivoVisible = useMemo(() => {
    const esArchivoReal =
      typeof File !== "undefined" &&
      archivo instanceof File;

    if (esArchivoReal) {
      return (
        documentAnalyzerService
          .obtenerInfoArchivo(archivo)
      );
    }

    return archivoPersistido;
  }, [
    archivo,
    archivoPersistido,
  ]);

  const hayArchivoVisible =
    Boolean(infoArchivoVisible);

  /*
   * Cuando el hook elimina el documento persistido,
   * también se limpia cualquier archivo local que
   * pudiera permanecer en este componente.
   */
  useEffect(() => {
    if (
      !archivoPersistido &&
      !analizando
    ) {
      setArchivo(null);
    }
  }, [
    archivoPersistido,
    analizando,
  ]);

  // ============================================================
  // LIMPIEZA
  // ============================================================

  const limpiarInput = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const limpiarArchivo = ({
    notificar = true,
  } = {}) => {
    setArchivo(null);
    setError(null);

    limpiarInput();

    if (
      notificar &&
      onArchivoEliminado
    ) {
      onArchivoEliminado();
    }
  };

  // ============================================================
  // PROCESAR ARCHIVO REAL
  // ============================================================

  const procesarArchivo = async (
    file
  ) => {
    setError(null);

    if (!file || analizando) {
      return;
    }

    const validacion =
      documentAnalyzerService
        .validarDocumento(file);

    if (!validacion.valido) {
      setError(
        validacion.mensaje
      );

      limpiarInput();

      return;
    }

    try {
      setArchivo(file);
      setAnalizando(true);

      /*
       * Informa al hook que existe una selección nueva.
       *
       * En este punto todavía no existe un file_id nuevo,
       * porque el análisis no ha terminado.
       */
      if (onArchivoSeleccionado) {
        onArchivoSeleccionado(
          file
        );
      }

      /*
       * La solicitud HTTP se realiza únicamente cuando
       * el usuario selecciona o arrastra un archivo real.
       *
       * archivoPersistido nunca pasa por esta función.
       */
      const resultado =
        await documentAnalyzerService
          .analizarDocumento(file);

      if (!resultado?.encontrado) {
        const mensaje =
          resultado?.mensaje ||
          (
            "No se pudieron identificar datos " +
            "curriculares en el documento."
          );

        setError(mensaje);
        setArchivo(null);

        limpiarInput();

        if (onArchivoEliminado) {
          onArchivoEliminado();
        }

        return;
      }

      /*
       * El resultado contiene los datos curriculares,
       * file_id, nombre normalizado del PDF y cantidad
       * de páginas.
       */
      if (onDocumentoAnalizado) {
        onDocumentoAnalizado(
          resultado
        );
      }
    } catch (err) {
      console.error(
        "Error analizando documento:",
        err
      );

      setError(
        err?.message ||
        "No se pudo analizar el documento."
      );

      setArchivo(null);

      limpiarInput();

      /*
       * La selección inicial bloquea temporalmente la
       * cantidad de sesiones.
       *
       * Si el análisis falla, se informa al hook para
       * limpiar todo el estado asociado al documento.
       */
      if (onArchivoEliminado) {
        onArchivoEliminado();
      }
    } finally {
      setAnalizando(false);
    }
  };

  // ============================================================
  // EVENTOS DEL INPUT
  // ============================================================

  const handleInputChange = (
    event
  ) => {
    const file =
      event.target.files?.[0];

    procesarArchivo(file);
  };

  const handleDrop = (
    event
  ) => {
    event.preventDefault();
    event.stopPropagation();

    /*
     * Mientras exista un archivo visible no se admite
     * otro por arrastre.
     *
     * El usuario debe eliminar primero el archivo actual.
     */
    if (
      analizando ||
      hayArchivoVisible
    ) {
      return;
    }

    const file =
      event.dataTransfer.files?.[0];

    procesarArchivo(file);
  };

  const handleDragOver = (
    event
  ) => {
    event.preventDefault();
  };

  const handleClickArea = () => {
    /*
     * El selector solo se abre cuando no existe un
     * archivo real ni uno persistido.
     */
    if (
      !analizando &&
      !hayArchivoVisible
    ) {
      inputRef.current?.click();
    }
  };

  const eliminarArchivo = (
    event
  ) => {
    event.preventDefault();
    event.stopPropagation();

    limpiarArchivo({
      notificar: true,
    });
  };

  // ============================================================
  // TEXTO VISUAL
  // ============================================================

  const nombreVisible =
    infoArchivoVisible?.nombre ||
    "Documento adjunto";

  const cantidadPaginas =
    infoArchivoVisible
      ?.cantidadPaginas ??
    infoArchivoVisible
      ?.cantidad_paginas ??
    null;

  const tamañoVisible =
    infoArchivoVisible?.tamaño ||
    null;

  const detallesArchivo = [];

  if (cantidadPaginas != null) {
    detallesArchivo.push(
      `${cantidadPaginas} ${
        Number(cantidadPaginas) === 1
          ? "página"
          : "páginas"
      }`
    );
  }

  if (tamañoVisible) {
    detallesArchivo.push(
      tamañoVisible
    );
  }

  const textoDetalle =
    detallesArchivo.length > 0
      ? (
          "Documento analizado correctamente · " +
          detallesArchivo.join(" · ")
        )
      : "Documento analizado correctamente";

  // ============================================================
  // RENDERIZADO
  // ============================================================

  return (
    <Box sx={{ mt: 4 }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: "#1e293b",
          mb: 2,
        }}
      >
        Documento de apoyo
      </Typography>

      {error && (
        <Alert
          severity="warning"
          sx={{
            mb: 2,
            borderRadius: "12px",
          }}
        >
          {error}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          ...uploadAreaSx,

          cursor:
            analizando ||
            hayArchivoVisible
              ? "default"
              : "pointer",
        }}
        onClick={handleClickArea}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          hidden
          accept=".doc,.docx,.pdf"
          onChange={handleInputChange}
          disabled={
            analizando ||
            hayArchivoVisible
          }
        />

        {analizando ? (
          <>
            <CircularProgress
              sx={{
                color: "#00c7db",
              }}
            />

            <Typography
              sx={{
                mt: 2,
                color: "#475569",
                fontWeight: 600,
              }}
            >
              Analizando documento con IA...
            </Typography>

            <Typography
              variant="caption"
              sx={{
                mt: 0.5,
                color: "#94a3b8",
              }}
            >
              Se están identificando los datos
              curriculares de la sesión.
            </Typography>
          </>
        ) : hayArchivoVisible ? (
          <Box sx={fileContainerSx}>
            <InsertDriveFileRoundedIcon
              sx={{
                fontSize: 42,
                color: "#00c7db",
              }}
            />

            <Box
              sx={{
                flex: 1,
                ml: 2,
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#1e293b",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {nombreVisible}
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  color: "#64748b",
                }}
              >
                {textoDetalle}
              </Typography>
            </Box>

            <IconButton
              onClick={eliminarArchivo}
              aria-label="Eliminar documento"
              disabled={analizando}
            >
              <DeleteRoundedIcon
                sx={{
                  color: "#ef4444",
                }}
              />
            </IconButton>
          </Box>
        ) : (
          <>
            <CloudUploadRoundedIcon
              sx={{
                fontSize: 50,
                color: "#00c7db",
              }}
            />

            <Typography
              sx={{
                mt: 1,
                fontWeight: 700,
                color: "#334155",
              }}
            >
              Arrastra tu archivo aquí
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "#64748b",
              }}
            >
              o haz clic para seleccionarlo
            </Typography>

            <Typography
              variant="caption"
              sx={{
                mt: 1,
                color: "#94a3b8",
              }}
            >
              Formatos permitidos: DOC, DOCX y PDF
            </Typography>

            <Typography
              variant="caption"
              sx={{
                mt: 0.5,
                color: "#94a3b8",
              }}
            >
              Máximo 20 páginas y 50 MB
            </Typography>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default FileUploadArea;